import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LegalAcceptanceCheckbox } from '@/components/legal/LegalAcceptanceCheckbox';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  companyId: string;
  onSuccess?: () => void;
  /** When true, the component is being used inside a modal/dialog (no internal header). */
  compact?: boolean;
  submitLabel?: string;
}

type RepresentativeRole = 'owner' | 'admin' | 'procurator';

export function CompanyContractAcceptanceForm({
  companyId,
  onSuccess,
  compact = false,
  submitLabel = 'Registrar aceite',
}: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [accepted, setAccepted] = useState(false);
  const [representativeRole, setRepresentativeRole] = useState<RepresentativeRole>('owner');
  const [representativeName, setRepresentativeName] = useState(
    (user?.user_metadata?.full_name as string | undefined) ?? '',
  );
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accepted) {
      toast.error('É necessário aceitar o Contrato B2B e o DPA');
      return;
    }
    if (!representativeName.trim()) {
      toast.error('Informe o nome do representante');
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        'record-company-contract-acceptance',
        {
          body: {
            company_id: companyId,
            representative_role: representativeRole,
            representative_name: representativeName.trim(),
          },
        },
      );
      if (error || (data as { error?: string })?.error) {
        throw new Error(
          (data as { error?: string })?.error ?? error?.message ?? 'unknown',
        );
      }
      await queryClient.invalidateQueries({ queryKey: ['company-contract-status', companyId] });
      toast.success('Aceite registrado com sucesso');
      onSuccess?.();
    } catch (err) {
      toast.error(`Não foi possível registrar o aceite: ${(err as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!compact && (
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <h3 className="font-semibold text-sm">
              Contrato B2B e Tratamento de Dados
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Você declara estar autorizado a representar a empresa e aceita o
              Contrato B2B e o Acordo de Processamento de Dados (DPA).
            </p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="representative-role" className="text-sm">
          Papel do representante
        </Label>
        <Select
          value={representativeRole}
          onValueChange={(v) => setRepresentativeRole(v as RepresentativeRole)}
        >
          <SelectTrigger id="representative-role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="owner">Sócio / Titular</SelectItem>
            <SelectItem value="admin">Administrador</SelectItem>
            <SelectItem value="procurator">Procurador</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="representative-name" className="text-sm">
          Nome do representante
        </Label>
        <Input
          id="representative-name"
          value={representativeName}
          onChange={(e) => setRepresentativeName(e.target.value)}
          placeholder="Nome completo"
        />
      </div>

      <LegalAcceptanceCheckbox
        id="b2b-contract-accept"
        checked={accepted}
        onCheckedChange={setAccepted}
      >
        Li e concordo com o{' '}
        <a
          href="/legal/b2b-contract"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2"
        >
          Contrato B2B
        </a>{' '}
        e{' '}
        <a
          href="/legal/dpa"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2"
        >
          DPA
        </a>
        .
      </LegalAcceptanceCheckbox>

      <Button
        type="submit"
        className="w-full"
        disabled={submitting || !accepted || !representativeName.trim()}
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Salvando...
          </>
        ) : (
          submitLabel
        )}
      </Button>
    </form>
  );
}
